import { FC, useContext, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import { useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { createLoadOptions } from "../../../../../hooks/useDebounce";
import useI18n from "../../../../../hooks/useTranslations";
import { AdminMembrane } from "../../../../../http/patrol/types/membranes";
import {
  AdminPatrolAnchorMapping,
  InsertAdminPatrolAnchorMappingBody,
  UpdateAdminPatrolAnchorMappingBody,
} from "../../../../../http/patrol/types/patrolAnchorMappings";
import { patrolHipsSpans } from "../../../../../http/patrol/types/patrolSettings";
import { AdminRoofSubstructure, RoofSubstructureSize } from "../../../../../http/patrol/types/roofSubstructures";
import { useMembranesApi } from "../../../../../http/patrol/useMembranes";
import { usePatrolAnchorMappingsApi } from "../../../../../http/patrol/usePatrolAnchorMappings";
import { useRoofSubstructuresApi } from "../../../../../http/patrol/useRoofSubstructures";
import { usePatrolSteelVersionsApi } from "../../../../../http/patrol/useSteelVersion";
import { AdminCommonSteelVersion } from "../../../../../http/types/commonSteelVersions";
import { SearchAdminItem } from "../../../../../http/types/items";
import { useItemsApi } from "../../../../../http/useItems";
import Form from "../../../../ui/Form";
import FormField from "../../../../ui/FormField";
import FormFieldsContainer from "../../../../ui/FormFieldsContainer";
import MultiCheckbox from "../../../../ui/MultiCheckbox";
import Numeric from "../../../../ui/Numeric";
import { CustomAsyncSelect, CustomSelect } from "../../../../ui/select/CustomSelect";
import styles from "./AnchorMappingForm.module.css";

interface AnchorMappingFormBody {
  roofSubstructure: AdminRoofSubstructure | null;
  material: AdminCommonSteelVersion | null;
  membrane: AdminMembrane | null;
  item: SearchAdminItem | null;
  extraItem: SearchAdminItem | null;
  minSubstructureSize: Partial<RoofSubstructureSize> | null;
  minSubstructureHeight: number | null;
  stepHeight: number | null;
  minStepHeight: number | null;
  maxStepHeight: number | null;
  seamHeight: number | null;
  maxSeamsSpan: number | null;
  maxInsulationThickness: number | null;
  hipsSpans: number[];
}

interface AnchorMappingDetailsProps {
  anchorMapping?: AdminPatrolAnchorMapping;
  onSave: () => void;
}

const AnchorMappingForm: FC<AnchorMappingDetailsProps> = ({ anchorMapping, onSave }: AnchorMappingDetailsProps) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const { patrolSystemId } = useParams<{ patrolSystemId: string }>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const aborter = useRef(new AbortController());
  const [defaultOptions, setDefaultOptions] = useState<SearchAdminItem[]>([]);

  const supportMaterialsApi = usePatrolSteelVersionsApi();
  const membraneApi = useMembranesApi();
  const roofSubstructureApi = useRoofSubstructuresApi();
  const anchorMappingsApi = usePatrolAnchorMappingsApi();
  const itemsApi = useItemsApi();

  const supportMaterialsResult = supportMaterialsApi.useAdminMaterials({}, aborter.current.signal);
  const membraneResult = membraneApi.useAdminMembranes({}, aborter.current.signal);
  const roofSubstructureResult = roofSubstructureApi.useAdminRoofSubstructures({}, aborter.current.signal);

  const onSubmit = () => async (formData: AnchorMappingFormBody) => {
    setIsSubmitting(true);

    const update: UpdateAdminPatrolAnchorMappingBody = {
      systemId: patrolSystemId!,
      roofSubstructureId: formData.roofSubstructure?.id,
      materialId: formData.material?.id,
      membraneId: formData.membrane?.id,
      extraItemId: formData.extraItem?.id,
      minSubstructureHeight: formData.minSubstructureHeight ?? undefined,
      minSubstructureSize:
        formData.minSubstructureSize?.height && formData.minSubstructureSize?.width
          ? {
              height: formData.minSubstructureSize.height,
              width: formData.minSubstructureSize.width,
            }
          : undefined,
      maxSeamsSpan: formData.maxSeamsSpan ?? undefined,
      maxInsulationThickness: formData.maxInsulationThickness ?? undefined,
      minStepHeight: formData.minStepHeight ?? undefined,
      maxStepHeight: formData.maxStepHeight ?? undefined,
      stepHeight: formData.stepHeight ?? undefined,
      seamHeight: formData.seamHeight ?? undefined,
      hipsSpans: formData.hipsSpans ?? [],
    };

    if (anchorMapping?.id) {
      try {
        await anchorMappingsApi.updateAdminPatrolAnchorMapping(anchorMapping.id, update);
        onSave();
      } catch (err) {
        dispatch(showErrorNotification(i18n, err));
      } finally {
        setIsSubmitting(false);
      }
    } else {
      const insert: InsertAdminPatrolAnchorMappingBody = {
        ...update,
        itemId: formData.item?.id!,
      };
      try {
        await anchorMappingsApi.insertAdminPatrolAnchorMapping(insert);
        onSave();
      } catch (err) {
        dispatch(showErrorNotification(i18n, err));
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const defaultValues: AnchorMappingFormBody = {
    roofSubstructure: anchorMapping?.roofSubstructure ?? null,
    material: anchorMapping?.material ?? null,
    membrane: anchorMapping?.membrane ?? null,
    item: anchorMapping?.item ?? null,
    extraItem: anchorMapping?.extraItem ?? null,
    minSubstructureSize: anchorMapping?.minSubstructureSize ?? null,
    minSubstructureHeight: anchorMapping?.minSubstructureHeight ?? null,
    stepHeight: anchorMapping?.stepHeight ?? null,
    minStepHeight: anchorMapping?.minStepHeight ?? null,
    maxStepHeight: anchorMapping?.maxStepHeight ?? null,
    seamHeight: anchorMapping?.seamHeight ?? null,
    maxSeamsSpan: anchorMapping?.maxSeamsSpan ?? null,
    maxInsulationThickness: anchorMapping?.maxInsulationThickness ?? null,
    hipsSpans: anchorMapping?.hipsSpans ?? [],
  };

  return (
    <Form<AnchorMappingFormBody>
      defaultValues={defaultValues}
      submitText={i18n.translation.common.save}
      disabled={isSubmitting}
      onSubmit={onSubmit}
      fullWidth
    >
      {({ control, setValue, formState: { errors }, reset, watch }) => {
        const hipsSpans = watch("hipsSpans");

        useEffect(() => {
          anchorMapping && reset(defaultValues);
        }, [anchorMapping]);

        return (
          <FormFieldsContainer fullWidth>
            <Controller
              name="item"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => {
                return (
                  <>
                    <FormField label={i18n.translation.items.singular} error={fieldState.error}>
                      {({ labelId }) => {
                        const searchTerm = useRef("");
                        const aborter = useRef(new AbortController());

                        const loadOptions = createLoadOptions(aborter, searchTerm, itemsApi.getAdminItems);

                        // Fetch initial data on mount
                        useEffect(() => {
                          loadOptions(searchTerm.current).then(setDefaultOptions);
                        }, []);

                        return (
                          <CustomAsyncSelect
                            {...field}
                            inputId={labelId}
                            defaultOptions={defaultOptions}
                            loadOptions={loadOptions}
                            isDisabled={!!anchorMapping?.id}
                            isClearable
                            getOptionLabel={(x) => x.code}
                            getOptionValue={(x) => x.id}
                            onChange={(value) => setValue("item", value ?? null)}
                          />
                        );
                      }}
                    </FormField>
                  </>
                );
              }}
            />
            <Controller
              name="extraItem"
              control={control}
              render={({ field, fieldState }) => {
                return (
                  <>
                    <FormField label={i18n.translation.common.extraItem} error={fieldState.error} isOptional>
                      {({ labelId }) => {
                        const searchTerm = useRef("");
                        const aborter = useRef(new AbortController());

                        const loadOptions = createLoadOptions(aborter, searchTerm, itemsApi.getAdminItems);

                        return (
                          <CustomAsyncSelect
                            {...field}
                            inputId={labelId}
                            defaultOptions={defaultOptions}
                            loadOptions={loadOptions}
                            isDisabled={!!anchorMapping?.id}
                            isClearable
                            getOptionLabel={(x) => x.code}
                            getOptionValue={(x) => x.id}
                            onChange={(value) => setValue("extraItem", value ?? null)}
                          />
                        );
                      }}
                    </FormField>
                  </>
                );
              }}
            />
            <Controller
              name="roofSubstructure"
              control={control}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.roofSubstructures.singular} error={fieldState.error} isOptional>
                  {({ labelId }) => (
                    <CustomSelect<AdminRoofSubstructure>
                      {...field}
                      inputId={labelId}
                      options={roofSubstructureResult.data?.items ?? []}
                      isDisabled={!!roofSubstructureResult.error || isSubmitting}
                      isLoading={roofSubstructureResult.isLoading}
                      isClearable
                      getOptionValue={(x) => x.id}
                      getOptionLabel={(x) => x.name}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="material"
              control={control}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.materials.singular} error={fieldState.error} isOptional>
                  {({ labelId }) => (
                    <CustomSelect<AdminCommonSteelVersion>
                      {...field}
                      inputId={labelId}
                      options={supportMaterialsResult.data?.items ?? []}
                      isDisabled={!!supportMaterialsResult.error || isSubmitting}
                      isLoading={supportMaterialsResult.isLoading}
                      getOptionValue={(x) => x.id}
                      getOptionLabel={(x) => x.name}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="membrane"
              control={control}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.membranes.singular} error={fieldState.error} isOptional>
                  {({ labelId }) => (
                    <CustomSelect<AdminMembrane>
                      {...field}
                      inputId={labelId}
                      options={membraneResult.data?.items ?? []}
                      isDisabled={!!membraneResult.error || isSubmitting}
                      isLoading={membraneResult.isLoading}
                      getOptionValue={(x) => x.id}
                      getOptionLabel={(x) => x.name}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="minSubstructureHeight"
              control={control}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.minSubstructureHeight} error={fieldState.error} isOptional>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      disabled={isSubmitting}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      onBlur={(value) => setValue("minSubstructureHeight", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <FormField
              label={i18n.translation.common.minSubstructureSize}
              error={errors.minSubstructureSize?.height || errors.minSubstructureSize?.width}
              isOptional
            >
              {({ labelId }) => (
                <div className={styles.substructureSize}>
                  <span>{i18n.translation.common.height}:</span>
                  <Controller
                    name="minSubstructureSize.height"
                    control={control}
                    rules={{
                      validate: (value) => {
                        const width = watch("minSubstructureSize.width");
                        if ((!value || value === 0) && (!!width || width === 0)) {
                          return i18n.translation.common.pleaseSpecifyAHeight;
                        }
                        return undefined;
                      },
                    }}
                    render={({ field }) => (
                      <Numeric
                        {...field}
                        id={labelId + "_height"}
                        disabled={isSubmitting}
                        isInvalid={!!errors.minSubstructureSize?.height}
                        decimalScale={0}
                        allowNegative={false}
                        suffix=" mm"
                        onBlur={(value) => setValue("minSubstructureSize.height", value ?? undefined)}
                      />
                    )}
                  />
                  <span>{i18n.translation.common.width}:</span>
                  <Controller
                    name="minSubstructureSize.width"
                    control={control}
                    rules={{
                      validate: (value) => {
                        const height = watch("minSubstructureSize.height");
                        if ((!value || value === 0) && (!!height || height === 0)) {
                          return i18n.translation.common.pleaseSpecifyAWidth;
                        }
                        return undefined;
                      },
                    }}
                    render={({ field }) => (
                      <Numeric
                        {...field}
                        id={labelId + "_wminSubstructureSize.width"}
                        disabled={isSubmitting}
                        isInvalid={!!errors.minSubstructureSize?.width}
                        decimalScale={0}
                        allowNegative={false}
                        suffix=" mm"
                        onBlur={(value) => setValue("minSubstructureSize.width", value ?? undefined)}
                      />
                    )}
                  />
                </div>
              )}
            </FormField>
            <Controller
              name="stepHeight"
              control={control}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.stepHeight} error={fieldState.error} isOptional>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      disabled={isSubmitting}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      onBlur={(value) => setValue("stepHeight", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <FormField
              label={i18n.translation.common.stepHeightRange}
              error={errors.minStepHeight || errors.maxStepHeight}
              isOptional
            >
              {({ labelId }) => (
                <div className={styles.substructureSize}>
                  <span>{i18n.translation.common.minHeight}:</span>
                  <Controller
                    name="minStepHeight"
                    control={control}
                    rules={{
                      validate: (value) => {
                        const max = watch("maxStepHeight");
                        if ((!value || value === 0) && (!!max || max === 0)) {
                          return i18n.translation.common.pleaseSpecifyAMaxHeight;
                        }
                        return undefined;
                      },
                    }}
                    render={({ field }) => (
                      <Numeric
                        {...field}
                        id={labelId}
                        disabled={isSubmitting}
                        isInvalid={!!errors.minStepHeight}
                        decimalScale={0}
                        allowNegative={false}
                        suffix=" mm"
                        onBlur={(value) => setValue("minStepHeight", value ?? null)}
                      />
                    )}
                  />
                  <span>{i18n.translation.common.maxHeight}:</span>
                  <Controller
                    name="maxStepHeight"
                    control={control}
                    rules={{
                      validate: (value) => {
                        const min = watch("minStepHeight");
                        if ((!value || value === 0) && (!!min || min === 0)) {
                          return i18n.translation.common.pleaseSpecifyAMinHeight;
                        }
                        return undefined;
                      },
                    }}
                    render={({ field }) => (
                      <Numeric
                        {...field}
                        id={labelId}
                        disabled={isSubmitting}
                        isInvalid={!!errors.maxStepHeight}
                        decimalScale={0}
                        allowNegative={false}
                        suffix=" mm"
                        onBlur={(value) => setValue("maxStepHeight", value ?? null)}
                      />
                    )}
                  />
                </div>
              )}
            </FormField>
            <Controller
              name="seamHeight"
              control={control}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.seamHeight} error={fieldState.error} isOptional>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      disabled={isSubmitting}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      onBlur={(value) => setValue("seamHeight", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="maxSeamsSpan"
              control={control}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.maxSeamsSpan} error={fieldState.error} isOptional>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      disabled={isSubmitting}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      onBlur={(value) => setValue("maxSeamsSpan", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="maxInsulationThickness"
              control={control}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.maxInsulationThickness} error={fieldState.error} isOptional>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      disabled={isSubmitting}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      onBlur={(value) => setValue("maxInsulationThickness", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <FormField label={i18n.translation.common.hipsSpans} isOptional>
              <MultiCheckbox
                disabled={isSubmitting}
                onUpdate={(id, checked) => {
                  if (checked) {
                    setValue("hipsSpans", [...hipsSpans, Number(id)]);
                  } else {
                    setValue(
                      "hipsSpans",
                      hipsSpans.filter((s) => s !== Number(id)),
                    );
                  }
                }}
                values={patrolHipsSpans.map((s) => ({
                  id: `${s.value}`,
                  title: `${s.value}`,
                  selected: hipsSpans.includes(s.value),
                }))}
              />
            </FormField>
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default AnchorMappingForm;
